import {useQuery} from "@tanstack/react-query";
import {GleamApi, GleamCompetitionActionsResponse} from "@devour/client";

async function fetchGleamActions(id: string): Promise<any> {
    // Make a direct API call to get the full response with type field
    const response = await fetch(`${import.meta.env.VITE_BACKEND_REST_URL}/gleam/competition-actions/${id}`);

    if (!response.ok) {
        // Check if it's a network error that might be recoverable
        if (response.status === 0 || !navigator.onLine) {
            throw new Error("Network connection lost. Please check your internet connection.");
        }
        throw new Error(`Failed to fetch Gleam actions: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data;
}

function useGleamActionsQuery(id: string) {
    return {
        queryKey: [
            "gleam-actions",
            id,
        ],
        queryFn: () => fetchGleamActions(id),
        staleTime: 60 * 60000,
        enabled: !!id && navigator.onLine, // Don't attempt if offline
        retry: (failureCount: number, error: any) => {
            // Retry up to 3 times for network errors
            if (failureCount >= 3) return false;

            // Retry network errors
            if (error?.message?.includes("Network") || error?.message?.includes("Failed to fetch")) {
                return true;
            }

            // Don't retry 4xx errors (client errors)
            if (error?.message?.includes("4")) {
                return false;
            }

            return true;
        },
        retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    } as const;
}

export function useGetGleamActions(id: string) {
    return useQuery(useGleamActionsQuery(id));
}